import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';
import { StatusActions, normalizeStatus, statusMeta, paidRSD, STATUS_KEYS } from './InvoiceStatus';
import { formatRSD, formatCur, fmtDate, toRSD } from '../lib/helpers';

const num = (n) => new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const PAGE_SIZES = [5, 10, 25, 50];

// Izveštaj za Excel (CSV sa ; i BOM-om da Excel ispravno prikaže č, ć, š, ž)
function downloadReport(client, docs, meta) {
  const head = ['Redni broj', 'Broj fakture', 'Datum fakture', 'Opis', 'Iznos', 'Valuta', 'Iznos (RSD)', 'Status', 'Naplaćeno (RSD)'];
  const rows = docs.map((d, i) => [
    i + 1,
    d.invoice_number || '',
    fmtDate(d.issue_date),
    (d.description || '').replace(/[\r\n]+/g, ' '),
    num(d.amount),
    d.type === 'domaca' || d.document_kind === 'fiskalni' ? 'RSD' : d.currency,
    num(toRSD(d)),
    meta[normalizeStatus(d.status)].label,
    num(paidRSD(d)),
  ]);
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = '\uFEFF' + [head, ...rows].map((r) => r.map(esc).join(';')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Izvestaj - ${String(client.name || 'klijent').replace(/[\\/:*?"<>|]+/g, '-')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Pregled klijenta: statistika, fakture / fiskalni računi, filteri po statusu
export default function ClientDetail() {
  const { id } = useParams();
  const { t, tr } = useI18n();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindTab, setKindTab] = useState('faktura'); // faktura | fiskalni
  const [statusFilter, setStatusFilter] = useState('sve');
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  useEffect(() => {
    (async () => {
      const [{ data: c }, { data: inv }] = await Promise.all([
        supabase.from('customers').select('*').eq('id', id).maybeSingle(),
        supabase.from('invoices').select('*').eq('customer_id', id).neq('document_kind', 'profaktura').order('issue_date', { ascending: true }),
      ]);
      setClient(c || null);
      setDocs(inv || []);
      setLoading(false);
    })();
  }, [id]);

  const meta = statusMeta(t);
  const todayISO = new Date().toISOString().slice(0, 10);
  const lateDays = (d) => {
    const st = normalizeStatus(d.status);
    if (!['poslato', 'delimicno'].includes(st) || !d.due_date || d.due_date >= todayISO) return 0;
    return Math.round((new Date(`${todayISO}T00:00:00`) - new Date(`${d.due_date}T00:00:00`)) / 86400000);
  };
  const matchStatus = (d, key) => {
    if (key === 'sve') return true;
    if (key === 'kasni') return lateDays(d) > 0;
    return normalizeStatus(d.status) === key;
  };
  const ofKind = useMemo(() => docs.filter((d) => d.document_kind === kindTab), [docs, kindTab]);

  const stats = useMemo(() => {
    const live = ofKind.filter((d) => normalizeStatus(d.status) !== 'stornirano');
    const invoiced = live.reduce((s, d) => s + toRSD(d), 0);
    const paid = live.reduce((s, d) => s + paidRSD(d), 0);
    return { invoiced, paid, unpaid: Math.max(invoiced - paid, 0), count: ofKind.length };
  }, [ofKind]);

  // Redni broj ide hronološki (1 = najstarija); lista prikazuje najnovije prvo
  const numbered = useMemo(() => ofKind.map((d, i) => ({ d, rb: i + 1 })).reverse(), [ofKind]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return numbered
      .filter(({ d }) => matchStatus(d, statusFilter))
      .filter(({ d }) => !q || [d.invoice_number, d.description].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [numbered, statusFilter, query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(0); }, [kindTab, statusFilter, query, pageSize]);

  if (loading) return <div style={styles.empty}>{t('loadingGeneric')}</div>;
  if (!client) return <div style={styles.empty}>{t('documentNotFound')}</div>;

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const onStatusChanged = (updated) => setDocs((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));

  const STAT_CARDS = [
    { label: t('statInvoiced'), value: `${num(stats.invoiced)} RSD`, icon: 'receipt' },
    { label: t('statPaid'), value: `${num(stats.paid)} RSD`, icon: 'checkCircle' },
    { label: t('statUnpaid'), value: `${num(stats.unpaid)} RSD`, icon: 'percent', warn: stats.unpaid > 0 },
    { label: t('statCount'), value: stats.count, icon: 'fileText' },
  ];
  const STATUS_TABS = [
    ['sve', t('stAll')],
    ...STATUS_KEYS.map((k) => [k, meta[k].label]),
    ['kasni', tr('Kasni', 'Overdue', 'Просрочено')],
  ];
  const countOf = (key) => ofKind.filter((d) => matchStatus(d, key)).length;
  const address = [client.address, [client.postal_code, client.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  return (
    <div style={styles.page}>
      <button onClick={() => navigate('/klijenti')} style={styles.backBtn}>
        <Icon name="arrowLeft" size={15} />
        {t('backToClients').replace('← ', '')}
      </button>

      {/* Naziv klijenta i dugmad */}
      <div style={styles.headRow}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h1 style={styles.heading}>{client.name}</h1>
          <div style={styles.sub}>
            {[client.pib && `${client.is_foreign ? 'Tax ID' : 'PIB'} ${client.pib}`,
              client.mb && `${client.is_foreign ? 'Company registration number' : 'MB'} ${client.mb}`,
              address, client.email].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div style={styles.btnRow}>
          <button onClick={() => downloadReport(client, ofKind, meta)} style={styles.outlineBtn} disabled={ofKind.length === 0}>
            <Icon name="download" size={15} />
            {t('downloadReport')}
          </button>
          <button onClick={() => navigate(`/klijenti/${id}/izmeni`)} style={styles.outlineBtn}>
            <Icon name="pencil" size={15} />
            {t('editClient')}
          </button>
          <button onClick={() => navigate(`/fakturisanje/nova?klijent=${id}`)} style={styles.primaryBtn}>
            <Icon name="plus" size={15} strokeWidth={2.2} />
            {t('newInvoice')}
          </button>
        </div>
      </div>

      {/* Četiri broja */}
      <div style={styles.statGrid}>
        {STAT_CARDS.map((c, i) => (
          <div key={c.label} style={styles.statCard} className={i === 0 ? 'glow-card' : undefined}>
            <span style={styles.statIcon}><Icon name={c.icon} size={18} color="var(--teal-800)" /></span>
            <div style={{ minWidth: 0 }}>
              <div style={styles.statLabel}>{c.label}</div>
              <div style={{ ...styles.statValue, ...(c.warn ? { color: 'var(--red-600)' } : {}) }}>{c.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Dokumenti */}
      <div style={styles.card}>
        <div style={styles.tabs} role="tablist">
          {[['faktura', t('tabInvoicesOnly')], ['fiskalni', t('tabFiscalOnly')]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={kindTab === k} onClick={() => setKindTab(k)} style={{ ...styles.tab, ...(kindTab === k ? styles.tabOn : {}) }}>{l}</button>
          ))}
        </div>

        <div style={styles.filterRow}>
          <div style={styles.chips}>
            {STATUS_TABS.map(([k, l]) => {
              const on = statusFilter === k;
              const late = k === 'kasni';
              return (
                <button key={k} onClick={() => setStatusFilter(k)} aria-pressed={on} style={{ ...styles.chip, ...(on ? styles.chipOn : {}), ...(late && !on ? { color: 'var(--red-600)' } : {}) }}>
                  {l}<span style={styles.chipCount}>{countOf(k)}</span>
                </button>
              );
            })}
          </div>
          <div style={styles.btnRow}>
            <label style={styles.searchBox}>
              <Icon name="search" size={15} color="var(--ink-500)" strokeWidth={1.9} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tr('Pretraži po broju ili opisu', 'Search by number or description', 'Поиск по номеру или описанию')} aria-label={t('searchClients')} style={styles.searchInput} />
            </label>
            <label style={styles.sizeBox}>
              <span>{t('showLabel')}</span>
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={styles.sizeSelect}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div style={styles.emptyInline}>{t('noClientDocuments')}</div>
        ) : (
          <>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 56 }}>{t('colSeqShort')}</th>
                    <th style={styles.th}>{t('colInvoiceNo')}</th>
                    <th style={styles.th}>{t('colInvoiceDate')}</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>{t('colTotal')}</th>
                    <th style={{ ...styles.th, paddingLeft: 24 }}>{t('colStatus')}</th>
                    <th style={{ ...styles.th, width: 90 }} aria-label="Akcije" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(({ d, rb }) => {
                    const st = normalizeStatus(d.status);
                    const isRsd = d.type === 'domaca' || d.document_kind === 'fiskalni';
                    const late = lateDays(d);
                    return (
                      <tr key={d.id} className="cd-row" onClick={() => navigate(`/fakturisanje/${d.id}/pregled`)} style={{ cursor: 'pointer' }}>
                        <td style={{ ...styles.td, color: 'var(--ink-500)' }}>{rb}</td>
                        <td style={styles.td}>
                          <div style={styles.no}>{d.invoice_number || '—'}</div>
                          {d.description && <div style={styles.desc}>{d.description}</div>}
                        </td>
                        <td className="mono" style={{ ...styles.td, whiteSpace: 'nowrap', color: 'var(--ink-700)' }}>{fmtDate(d.issue_date)}</td>
                        <td className="mono" style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, ...(st === 'stornirano' ? { textDecoration: 'line-through', color: 'var(--ink-500)' } : {}) }}>
                          {isRsd ? formatRSD(d.amount) : formatCur(d.amount, d.currency)}
                          {st === 'delimicno' && (
                            <div style={styles.paidNote}>{t('paidOfTotal', isRsd ? formatRSD(d.paid_amount) : formatCur(d.paid_amount, d.currency))}</div>
                          )}
                        </td>
                        <td style={{ ...styles.td, paddingLeft: 24 }}>
                          <span style={styles.status}>
                            <span style={{ ...styles.dot, background: late ? 'var(--red-600)' : meta[st].dot }} />
                            {late ? tr(`Kasni ${late} d.`, `${late} d overdue`, `Просрочено ${late} дн.`) : meta[st].label}
                          </span>
                        </td>
                        <td style={styles.td} onClick={(e) => e.stopPropagation()}><StatusActions invoice={d} onChange={onStatusChanged} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={styles.pager}>
              <span style={styles.pagerText}>{t('pageRange', safePage * pageSize + 1, Math.min((safePage + 1) * pageSize, filtered.length), filtered.length)}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setPage(safePage - 1)} disabled={safePage === 0} style={styles.pageBtn}><Icon name="arrowLeft" size={14} />{t('prevPage')}</button>
                <button onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount - 1} style={styles.pageBtn}>{t('nextPage')}<Icon name="chevronRight" size={14} /></button>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .cd-row:hover td { background: var(--paper); }
        @media (max-width: 760px) {
          .cd-row td { padding: 10px 10px !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  page: { display: 'flex', flexDirection: 'column', gap: 18 },
  empty: { textAlign: 'center', color: 'var(--ink-500)', fontSize: 'var(--fs-body, 14px)', padding: '40px 0' },
  backBtn: { alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', color: 'var(--ink-500)', fontWeight: 600, fontSize: 'var(--fs-table, 13px)', padding: 0, cursor: 'pointer' },
  headRow: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
  heading: { fontSize: 'var(--fs-page, 22px)', fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--ink-900)', margin: 0 },
  sub: { fontSize: 'var(--fs-table, 13px)', color: 'var(--ink-500)', marginTop: 4 },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 },
  statCard: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 },
  statIcon: { width: 38, height: 38, borderRadius: 8, background: 'var(--paper)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statLabel: { fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 'var(--fs-micro, 11px)', color: 'var(--ink-500)' },
  statValue: { fontSize: 'var(--fs-number, 20px)', fontWeight: 600, color: 'var(--ink-900)', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' },
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' },
  tabs: { display: 'flex', gap: 4, padding: '0 16px', borderBottom: '1px solid var(--line)' },
  tab: { border: 'none', borderBottom: '2px solid transparent', marginBottom: -1, background: 'none', padding: '12px 8px', fontSize: 'var(--fs-table, 13px)', fontWeight: 600, color: 'var(--ink-500)', cursor: 'pointer' },
  tabOn: { color: 'var(--ink-900)', fontWeight: 700, borderBottomColor: 'var(--teal-800)' },
  filterRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 16px', borderBottom: '1px solid var(--line)' },
  chips: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 30, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-700)', borderRadius: 7, padding: '0 10px', fontWeight: 600, fontSize: 'var(--fs-table, 13px)', cursor: 'pointer' },
  chipOn: { background: 'var(--teal-800)', borderColor: 'var(--teal-800)', color: '#fff' },
  chipCount: { fontSize: 'var(--fs-micro, 11px)', opacity: 0.7 },
  btnRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  outlineBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-900)', borderRadius: 7, padding: '0 12px', fontWeight: 600, fontSize: 'var(--fs-table, 13px)', cursor: 'pointer' },
  primaryBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 7, padding: '0 14px', fontWeight: 600, fontSize: 'var(--fs-table, 13px)', cursor: 'pointer' },
  searchBox: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 32, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 7, padding: '0 10px', minWidth: 200 },
  searchInput: { flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 'var(--fs-table, 13px)' },
  sizeBox: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 32, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 7, padding: '0 8px 0 10px', fontSize: 'var(--fs-table, 13px)', color: 'var(--ink-500)' },
  sizeSelect: { border: 'none', background: 'transparent', fontSize: 'var(--fs-table, 13px)', fontWeight: 600, color: 'var(--ink-900)' },
  emptyInline: { color: 'var(--ink-500)', fontSize: 'var(--fs-table, 13px)', padding: '28px 0', textAlign: 'center' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 680 },
  th: { textAlign: 'left', background: 'var(--paper)', padding: '0 16px', height: 36, borderBottom: '1px solid var(--line)', fontSize: 'var(--fs-micro, 11px)', fontWeight: 600, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '10px 16px', borderBottom: '1px solid var(--line)', fontSize: 'var(--fs-table, 13px)', color: 'var(--ink-900)', verticalAlign: 'middle' },
  no: { fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap' },
  desc: { fontSize: 'var(--fs-small, 12px)', color: 'var(--ink-500)', marginTop: 2, maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  status: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-table, 13px)', color: 'var(--ink-700)', whiteSpace: 'nowrap' },
  dot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  paidNote: { fontSize: 'var(--fs-micro, 11px)', fontWeight: 600, color: 'var(--gold-600)', marginTop: 2 },
  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px' },
  pagerText: { fontSize: 'var(--fs-small, 12px)', color: 'var(--ink-500)' },
  pageBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 32, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 7, padding: '0 10px', fontWeight: 600, fontSize: 'var(--fs-table, 13px)', color: 'var(--ink-700)', cursor: 'pointer' },
};
